import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Session storage key to remember demo access was granted
const DEMO_ACCESS_KEY = 'illumine_demo_access_granted';

export default function ProtectedRoute({ children }) {
  const { user, loading, hasActiveSubscription } = useAuth();
  const [searchParams] = useSearchParams();

  // Check if demo mode is requested via URL parameter
  // Use both React Router's searchParams AND native URL parsing for reliability on hard reloads
  const urlSearchParams = new URLSearchParams(window.location.search);
  const isDemoFromRouter = searchParams.get('demo') === 'true';
  const isDemoFromWindow = urlSearchParams.get('demo') === 'true';
  const isDemoFromURL = isDemoFromRouter || isDemoFromWindow;

  // Check if demo access was previously granted in this session
  // This prevents redirect when useDemoSession removes the ?demo parameter
  const isDemoAccessGranted = sessionStorage.getItem(DEMO_ACCESS_KEY) === 'true';

  // If we see demo=true in URL, remember it for this session
  if (isDemoFromURL && !isDemoAccessGranted) {
    sessionStorage.setItem(DEMO_ACCESS_KEY, 'true');
  }

  const isDemoRequest = isDemoFromURL || isDemoAccessGranted;

  // Check if there's an existing demo session in localStorage
  const hasExistingDemoSession = () => {
    try {
      const stored = localStorage.getItem('illumine_demo_session');
      if (stored) {
        const session = JSON.parse(stored);
        const elapsed = session.totalUsed || 0;
        const DEMO_DURATION_MS = 15 * 60 * 1000; // 15 minutes
        return elapsed < DEMO_DURATION_MS;
      }
    } catch (e) {
      console.error('Error checking demo session:', e);
    }
    return false;
  };

  // Check if Supabase is configured
  const isSupabaseConfigured =
    import.meta.env.VITE_SUPABASE_URL &&
    import.meta.env.VITE_SUPABASE_ANON_KEY &&
    import.meta.env.VITE_SUPABASE_URL !== 'https://placeholder.supabase.co';

  // If Supabase is not configured, allow access (development mode)
  if (!isSupabaseConfigured) {
    console.warn('⚠️ Supabase not configured - Running in development mode without authentication');
    return children;
  }

  // Show loading only during initial auth check
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  // Allow access if:
  // 1. User is authenticated with active subscription
  // 2. Demo mode is requested via URL (always allow - useDemoSession will handle expiration modal)
  // 3. User has an existing valid demo session
  if (user && hasActiveSubscription?.()) {
    return children;
  }

  // IMPORTANT: Always allow access when demo is requested via URL
  // The useDemoSession hook will handle showing expiration modal if needed
  if (isDemoRequest) {
    return children;
  }

  if (hasExistingDemoSession()) {
    return children;
  }

  // If user is not authenticated and no demo, redirect to landing page
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated but no subscription - allow access (they can use free generations or demo)
  return children;
}
